"use client";

// React Required
import React, { useEffect, useRef, useState } from "react";

// External Lib
import { Box } from "@mui/material";

// Internal Lib
import { AutoComplete, Input, Label, showToast, Dropdown, DropdownAutoSuggest } from "@int-traderjoes/ui-components-library";

// Contexts
import { useTheme } from "@/context";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";

// types import
import {
  AuditTrailEntry,
  IVendorInformationFormData,
  MelissaAddressSuggestion,
  DropdownItem,
} from "@/context/data.types";
import { VendorInformationFormData } from "@/context/data";

// Constants
import Constants from "@/static/constants/constants.json";

// Utils
import FormHeader from "@/utils/FormHeader";
import {
  convertUtcToLocalDateTime,
  isValidEmail,
  isValidTwoDecimalNumber,
  formatRawDropdownOptions,
  handleAllErrorFields,
  sanitizeVendorCorporateName,
} from "@/utils/helper";

// Services
import AuthService from "@/services/authService";
import VendorService from "@/services/vendorService";

//Assets
import { DownArrow, UpArrow } from "@/assets/images/CommonSvgUtils";

// Styles
import "./vendor-informationForm.scss";
import ConfirmationModal from "../ConfirmationModal";
import {
  handleSuggestionsFetchRequested,
  setAddressConfirmationMessage,
} from "@/services/external/mellisa";
import { handleApiError } from "@/hooks/useApiErrorHandler";
import ProductService from "@/services/productService";
import { getMelissaCountryName, getDropdownCountryName } from "@/utils/countryMapping";

const ADDR_RECORDS_COUNT =
  process.env.NEXT_PUBLIC_MELISSA_ADDRESS_SUGGESTIONS_DEFAULT_COUNT ?? "10";

interface VendorInformationFormProps {
  vendorInformationFormData?: IVendorInformationFormData;
  vendorKey: number;
  isVendorAssessmentScreen?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  formUpdated: () => void;
  setWorkflowButtons?: React.Dispatch<React.SetStateAction<any>>;
  isFormDisabled?: boolean;
  vendorOnloadDropdownData?: DropdownData | null | undefined;
}

export interface DropdownData {
  logistics: any[];
  retails: any[];
  countries: any[];
  [key: string]: any;
}

// Prevent duplicate getVendorInfoOnLoadData API calls across effect re-runs / remounts
let vendorOnloadDataFetchInFlight = false;
let vendorOnloadDataFetchPromise: Promise<any> | null = null;

const VendorInformationForm: React.FC<VendorInformationFormProps> = ({
  vendorInformationFormData,
  vendorKey,
  isVendorAssessmentScreen,
  isExpanded,
  onToggle,
  formUpdated,
  setWorkflowButtons,
  isFormDisabled,
  vendorOnloadDropdownData,
}) => {
  // If no data is passed then setting with the defaultvalues form context
  if (!vendorInformationFormData) {
    vendorInformationFormData = VendorInformationFormData;
  }

  const { darkMode } = useTheme();
  const { languageText } = useLanguageContext();
  const messageTexts = languageText?.MESSAGES;
  const toastMessageTexts = languageText?.MESSAGES?.TOAST_MESSAGES;
  const vendorInformationLabels = languageText?.LABELS?.VENDOR_INFORMATION?.VENDOR_INFORMATION_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.VENDOR_INFORMATION;
  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const payloadRef = useRef<Record<string, string>>({ maxrecords: ADDR_RECORDS_COUNT }); // Persistent object
  const [addressSuggestions, setAddressSuggestions] = useState<MelissaAddressSuggestion[]>([]);
  const [dropdownData, setDropdownData] = useState<DropdownData>(() => 
    vendorOnloadDropdownData ?? {
      logistics: [],
      retails: [],
      countries: [],
    }
  );

  const {
    setShowCreateProduct,
    hasVendorInformationFormsSubmitted,
    vendorUpdated,
    setVendorUpdated,
    setIsLoading,
    setUserLogs,
    setCommentCount,
    shoppingListComments,
    setShoppingListComments
  } = useGlobalStateContext();
  const {
    vendorInformationForms,
    setVendorInformationForms,
    vendorInformationFormErrors,
    setVendorInformationFormErrors,
    selectedIdea,
    statusesOfProduct,
  } = useFormContext();

  const displayMode = () => {
    if (darkMode) {
      return "dark";
    } else {
      return "light";
    }
  };
  // Ref to store the previous address object
  const previousAddressRef = useRef({
    street: "",
    city: "",
    state: "",
    // country: "",
    postalCode: "",
  });

  const vendorId = vendorInformationForms[vendorKey]?.id;

  // Per-vendor: true when this vendor was selected as "Existing Vendor" in Add Vendor modal (stored in vendorAssessment)
  const isExistingVendor = vendorInformationFormData?.vendorAssessment?.hasExistingVendor === true;

  // Initialize payload with country if available
  useEffect(() => {
    if (vendorInformationFormData?.vendorCountry && dropdownData.countries.length > 0) {
      const countryId = vendorInformationFormData.vendorCountry;
      const countryName = dropdownData.countries.find((c: any) => 
        (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
      )?.name;
      
      if (countryName) {
        // Map country name for Melissa API compatibility
        const melissaCountryName = getMelissaCountryName(countryName);
        updatePayload("vendorCountry", melissaCountryName);
      }
    }
  }, [vendorInformationFormData?.vendorCountry, dropdownData.countries]);

  // Fetch Field Permissions for Vendor Information
  let permissions: any = {};

  permissions = AuthService.fetchPermissions("VendorInformation");

  const vendorInfoFormErrors = vendorInformationFormErrors[vendorKey];

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  const handleConfirm = () => {
    handleCloseModal();
  };

  const handleSelectAlternative = () => {
    // handleSuggestionsFetchRequested()
    setTimeout(() => {
      const inputElement = document.getElementById("vendor-corporate-address-input");
      inputElement?.focus();
    }, 300); // Delay to allow modal to close and input to mount
    handleCloseModal();
  };

  const isFieldDisabled = (fieldName: string) => {
    if (isFormDisabled) return true;
    // return false;
    let isDisabled =
      !selectedIdea.editable ||
      statusesOfProduct?.workflowStatus?.toLowerCase() === Constants?.ON_HOLD_STATUS.toLowerCase() ||
      statusesOfProduct?.workflowStatus?.toLowerCase() === Constants?.ARCHIVED_STATUS.toLowerCase() ||
      permissions[fieldName]?.isEditable === false;

    return isDisabled;
  };

  // Handler function for AutoComplete input change

  const updatePayload = (field: keyof typeof VendorInformationFormData, value: string) => {
    // Map fields to corresponding payload keys
    const fieldMap: { [key in keyof typeof VendorInformationFormData]: string } = {
      vendorCorporateAddress: "address1",
      vendorCity: "locality",
      vendorState: "administrativearea",
      vendorCountry: "country",
      vendorZipCode: "postalcode",
      vendorCorporateName: "",
      vendorFirstName: "",
      vendorLastName: "",
      vendorEmailAddress: "",
      subStatus: "",
      logistics: "",
      retailStructure: "",
      tariff: "",
      multipack: "",
    };

    const payloadKey = fieldMap[field];

    if (payloadKey) {
      payloadRef.current = { ...payloadRef.current, [payloadKey]: value }; // Merge old and new values
    }
  };

  const handleAutoCompleteInputChange =
    (field: keyof typeof VendorInformationFormData) => (newValue: string) => {
      updatePayload(field, newValue);
      
      // Also update the payload with the current country value for better suggestions
      if (vendorInformationFormData?.vendorCountry && dropdownData.countries.length > 0) {
        const countryId = vendorInformationFormData.vendorCountry;
        const countryName = dropdownData.countries.find((c: any) => 
          (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
        )?.name;
        
        if (countryName) {
          // Map country name for Melissa API compatibility
          const melissaCountryName = getMelissaCountryName(countryName);
          updatePayload("vendorCountry", melissaCountryName);
        }
      }

      //create updated field object
      let updateInfo = {
        [vendorKey]: {
          [field]: newValue,
        },
      };

      // update vendor form values state variable
      setVendorInformationForms(prevForms => {
        const updatedForms = [...prevForms];
        const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
        updatedForms[vendorKey] = {
          ...currentVendorForm,
          ...updateInfo[vendorKey],
        };
        return updatedForms;
      });

      // store updated fields
      storeVendorFormUpdates(updateInfo, vendorKey);

      formUpdated();
      handleAllErrorFields(vendorInfoFormErrors, setWorkflowButtons);
    };

  // Handler function for when a suggestion is selected
  const onSuggestionSelected = (
    event: React.FormEvent,
    { suggestion }: { suggestion: MelissaAddressSuggestion }
  ) => {
    const selectedAddress = suggestion.Address;
    
    // Find the country ID that matches the country from Melissa API
    // Try to match by ISO3166_N (numeric country code) first, then by name
    let countryMatch = dropdownData?.countries.find(
      country => country.id === selectedAddress?.ISO3166_N
    );
    
    // If no match by ISO code, try matching by country name
    if (!countryMatch) {
      countryMatch = dropdownData?.countries.find(
        country => country.name === selectedAddress?.CountryName
      );
    }
    
    // If still no match, try reverse mapping (Melissa "United States" -> dropdown "United States Of America")
    if (!countryMatch) {
      const mappedCountryName = getDropdownCountryName(selectedAddress?.CountryName);
      countryMatch = dropdownData?.countries.find(
        country => country.name === mappedCountryName
      );
    }

      const updateInfo = {
      vendorCorporateAddress: selectedAddress.Address1,
      vendorCity: selectedAddress.Locality,
      vendorState: selectedAddress.AdministrativeArea,
      // Don't update country - keep the user's selection since we're filtering by country
      vendorZipCode: selectedAddress.PostalCode,
    };
    setVendorInformationForms(prevForms => {
      const updatedForms = [...prevForms];
      const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
      updatedForms[vendorKey] = {
        ...currentVendorForm,
        ...updateInfo,
      };
      return updatedForms;
    });
      
      setVendorInformationFormErrors(prevForms => {
        const updatedForms = [...prevForms];
        updatedForms[vendorKey] = {
          ...updatedForms?.[vendorKey],
          vendorCity: false,
          vendorState: false,
          vendorCountry: false,
          vendorZipCode: false,
        };
        return updatedForms;
      });
    // store updated fields
    storeVendorFormUpdates(updateInfo, vendorKey);
    formUpdated();
  };

  const onSuggestionsClearRequested = (): void => {
    setAddressSuggestions([]);
  };

  // Function to store vendor form updates in context and local storage
  const storeVendorFormUpdates = (updateInfo: any, vendorKey: number) => {
    // check from local storage if exist
    const productKey = selectedIdea?.ideaId
      ? "productInfo" + selectedIdea.ideaId
      : "newProductInfo";
    const productUpdates = JSON.parse(localStorage.getItem(productKey) ?? "{}");
    if (!productUpdates.vendors) {
      productUpdates.vendors = [];
    }
    // if current vendor updates does not exist create new one
    if (!productUpdates.vendors[vendorKey]) {
      productUpdates.vendors[vendorKey] = {};
    }
      // update vendor object
      Object.assign(productUpdates?.vendors[vendorKey], updateInfo[vendorKey]);
      // store vendor updates back to local storage
      localStorage.setItem(productKey, JSON.stringify(productUpdates));
    setVendorUpdated(false);
    setTimeout(() => setVendorUpdated(true), 0);
    // setVendorUpdated(true);
    // update state object "vendorInfoUpdates" and trigger save info
    // setVendorInfoUpdates((prevUpdates) => {

    //   const updates = [...prevUpdates];
    //   updates[vendorKey] = {
    //     ...updates[vendorKey],
    //     ...updateInfo[vendorKey]
    //   }
    //   console.log('updates', updates);
    //   return updates;
    // })
  };

  /** Shared: clear address fields when country changes or is cleared (existing country-change behavior). */
  const clearAddressFieldsDependentOnCountry = () => {
    setAddressSuggestions([]);
    setVendorInformationForms(prevForms => {
      const updatedForms = [...prevForms];
      const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
      updatedForms[vendorKey] = {
        ...currentVendorForm,
        vendorCorporateAddress: "",
        vendorCity: "",
        vendorState: "",
        vendorZipCode: "",
      };
      return updatedForms;
    });
    updatePayload("vendorCorporateAddress", ""); // resetting address from auto-complete call
  };

  const handleDropdownChange =
    (index: number, field: keyof IVendorInformationFormData) =>
    (e: any, option?: DropdownItem | null, selectedValues?: any[]) => {
      const isCleared =
        option == null || option.value === "" || option.value == null;

      if (isCleared) {
        // Use "" (not null) so vendor save payload can send an explicit clear the BE accepts
        setVendorInformationForms(prevForms => {
          const updatedForms = [...prevForms];
          const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
          updatedForms[vendorKey] = {
            ...currentVendorForm,
            [field]: "" as any,
          };
          return updatedForms;
        });
        if (field === "vendorCountry") {
          clearAddressFieldsDependentOnCountry();
          storeVendorFormUpdates(
            {
              [vendorKey]: {
                vendorCountry: "",
                vendorCorporateAddress: "",
                vendorCity: "",
                vendorState: "",
                vendorZipCode: "",
              },
            },
            vendorKey
          );
        } else {
          storeVendorFormUpdates({ [vendorKey]: { [field]: "" } }, vendorKey);
        }
        formUpdated();
        handleAllErrorFields(vendorInfoFormErrors, setWorkflowButtons);
        return;
      }

      if (option) {
        // For country dropdown, store the ID (value) not the label (name)
        // For other dropdowns, use the value as well for consistency
        const fieldValue = option.value; // Keep original type (number)
        
        // create updated field object
        let updateInfo = {
          [vendorKey]: {
            [field]: fieldValue,
          },
        };
        
        // update the vendorInformationFormData state
        setVendorInformationForms(prevForms => {
          const updatedForms = [...prevForms];
          const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
          updatedForms[vendorKey] = {
            ...currentVendorForm,
            [field]: fieldValue,
          };
          return updatedForms;
        });

        // Reuse same address clear when country selection changes
        if (field === "vendorCountry") {
          clearAddressFieldsDependentOnCountry();
        }

        // store updated fields
        storeVendorFormUpdates(updateInfo, vendorKey);
        formUpdated();
      }
      handleAllErrorFields(vendorInfoFormErrors, setWorkflowButtons);
    };

  const handleInputChange =
    (field: keyof IVendorInformationFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let targetValue = e.target.value;

      // Sanitize vendorCorporateName, vendorFirstName, and vendorLastName to prevent characters that could break base64 encoding/decoding
      if (field === "vendorCorporateName" || field === "vendorFirstName" || field === "vendorLastName") {
        const originalValue = targetValue;
        targetValue = sanitizeVendorCorporateName(targetValue);
        
      
      }

      //create updated field object
      let updateInfo = {
        [vendorKey]: {
          [field]: targetValue,
        },
      };

      // update vendor form values state variable
      setVendorInformationForms(prevForms => {
        const updatedForms = [...prevForms];
        const currentVendorForm = updatedForms?.[vendorKey] ?? VendorInformationFormData;
        updatedForms[vendorKey] = {
          ...currentVendorForm,
          ...updateInfo[vendorKey],
        };
        return updatedForms;
      });

      // store updated fields
      storeVendorFormUpdates(updateInfo, vendorKey);

      formUpdated();
      handleAllErrorFields(vendorInfoFormErrors, setWorkflowButtons);
    };

  const handleFieldFocus = (field: keyof IVendorInformationFormData) => {
    const updatedErrors = [...vendorInformationFormErrors];

    updatedErrors[vendorKey] = {
      ...updatedErrors[vendorKey], // Spread the previous errors for this tab
      [field]: false,
    };

    setVendorInformationFormErrors(updatedErrors);
  };

  const handleBlur = async (field: keyof IVendorInformationFormData) => {
    const updatedErrors = [...vendorInformationFormErrors];
    if (
      hasVendorInformationFormsSubmitted &&
      !vendorInformationFormData[field] &&
      permissions[field].isMandatory
    ) {
      updatedErrors[vendorKey] = {
        ...updatedErrors[vendorKey], // Spread the previous errors for this tab
        [field]: true,
      };
    }

    // Address validation for Melissa API (excluding country field)
    if (["vendorCity", "vendorState", "vendorZipCode"].includes(field)) {
      const address = {
        street: vendorInformationFormData.vendorCorporateAddress ?? "",
        city: vendorInformationFormData.vendorCity ?? "",
        state: vendorInformationFormData.vendorState ?? "",
        // country: vendorInformationFormData.vendorCountry ?? "",
        postalCode: vendorInformationFormData.vendorZipCode ?? "",
      };
      // Compare current address with the previous address
      const hasAddressChanged = Object.keys(address).some(
        key =>
          address[key as keyof typeof address] !==
          previousAddressRef.current[key as keyof typeof address]
      );
      if (hasAddressChanged) {
        // setIsLoading(true);
        // try {
        //   const isValid = await validateAddress(address, messageTexts, setIsLoading);
        //   if (isValid) {
        //     setIsModalOpen(true);
        //     // handleOpenModal()
        //   } else {
        //     showToast({
        //       type: "error",
        //       message:
        //         toastMessageTexts?.MELISSA_ADDRESS_VALIDATION
        //           ?.GLOBAL_ADDRESS_VALIDATION_API_ERROR,
        //     });
        //     setIsModalOpen(true);
        //   }
        // } catch (error) {
        //   console.error("Error validating address:", error);
        // } finally {
        //   setIsLoading(false);
        // }
        setAddressConfirmationMessage(messageTexts);
        setIsModalOpen(true);
        // Update the previous address ref
        previousAddressRef.current = address;
      }
      if (field === "vendorZipCode") {
        const vendorZipCodeValue = (vendorInformationFormData[field] ?? "") as string;
        if (vendorZipCodeValue && vendorZipCodeValue.trim().length < Constants.ZIP_CODE_MIN_LENGTH) {
          updatedErrors[vendorKey] = {
            ...updatedErrors[vendorKey], // Spread the previous errors for this tab
            [field]: true,
          };
          showToast({
            type: "error",
            message: errorTexts?.ZIP_CODE_MIN_LENGTH_ERROR
          });
        }
      }
      // if (resultCode) {
      //  if (
      //   resultCode.some((code : string) => code.startsWith("AE")) ||
      //   resultCode.some((code : string) => code.startsWith("AC"))
      // ) {
      //     setConfirmationMessage(
      //       messagesText?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_MESSAGE?.CP_SLM_VI_ADDRESS_NOT_FOUND_CONFIRM_MSG
      //     );
      //     setConfirmationTittle(messagesText?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_TITLE?.CP_SLM_VI_ADDRESS_NOT_FOUND_CONFIRM_TITLE);
      //     setButtonActionClicked( messagesText?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_ACTION?.CP_SLM_VI_ADDRESS_NOT_FOUND_CONFIRM_BUTTON_ACTION);
      //     setButtonActionClicked( messagesText?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_ACTION?.CP_SLM_VI_ADDRESS_NOT_FOUND_CANCEL_BUTTON_ACTION);
      //     handleOpenModal()
      //   }
      // }
    } else if (field === "vendorEmailAddress") {
      const emailValue = (vendorInformationFormData[field] ?? "") as string;
      if (emailValue.trim() !== "" && !isValidEmail(emailValue)) {
        updatedErrors[vendorKey] = {
          ...updatedErrors[vendorKey], // Spread the previous errors for this tab
          [field]: true,
        };
        showToast({
          type: "error",
          message: Constants.EMAIL_FORMAT_ERROR_MESSAGE,
        });
      }
    }
    setVendorInformationFormErrors(updatedErrors);
  };

  useEffect(() => {
    setTimeout(() => setShowCreateProduct(true), 0);
    return () => {
      setShowCreateProduct(false);
    };
  }, [setShowCreateProduct]);

  useEffect(() => {
    let key = selectedIdea?.ideaId ? "vendorInfo" + selectedIdea.ideaId : "newVendorInfo";
    localStorage.setItem(key, JSON.stringify(vendorInformationForms));
  }, [vendorInformationForms, selectedIdea]);

  const getOnloadData = async () => {
    if (dropdownData?.logistics?.length === 0 && dropdownData?.retails?.length === 0 && dropdownData?.countries?.length === 0) {
      if (vendorOnloadDataFetchInFlight && vendorOnloadDataFetchPromise) {
        return vendorOnloadDataFetchPromise;
      }
      vendorOnloadDataFetchInFlight = true;
      vendorOnloadDataFetchPromise = (async () => {
        try {
          setIsLoading(true);
          const resp = await VendorService.getVendorInfoOnLoadData();
          setDropdownData(resp);
          return resp;
        } catch (error) {
          handleApiError(error, toastMessageTexts?.FETCH_DATA_ERROR);
          throw error;
        } finally {
          setIsLoading(false);
          vendorOnloadDataFetchInFlight = false;
          vendorOnloadDataFetchPromise = null;
        }
      })();
      return vendorOnloadDataFetchPromise;
    }
  };

  // Sync dropdown data when prop changes
  useEffect(() => {
    if (vendorOnloadDropdownData) {
      setDropdownData(vendorOnloadDropdownData);
    }
  }, [vendorOnloadDropdownData]);

  // Only fetch if parent didn't provide data (undefined means not using parent fetch)
  useEffect(() => {
    if (vendorOnloadDropdownData === undefined) {
      getOnloadData();
    }
  }, [vendorOnloadDropdownData]);

  useEffect(() => {
    if(!vendorId) return;
    
    // Use comments from context instead of making API call
    if (shoppingListComments && shoppingListComments.length > 0) {
      setCommentCount(shoppingListComments.length);
    } else {
      // If no cached comments, set count to 0
      setCommentCount(0);
    }
  }, [vendorId, shoppingListComments]);

  return (
    <Box
      className={`bg-white dark:bg-TJ-Dark2 border-TJ-Box-Border dark:border-TJ-Dark border ${
        isExpanded && isVendorAssessmentScreen ? "border-TJ-Natural" : ""
      }`}
    >
      <Box
        className={`w-full h-10 bg-TJ-Box-Light-Grey dark:bg-TJ-Dark3 flex items-center border-b border-b-TJ-Box-Border dark:border-b-TJ-Dark justify-between cursor-pointer ${
          isExpanded && isVendorAssessmentScreen ? "bg-TJ-Natural" : ""
        }`}
        onClick={onToggle}
      >
        <FormHeader title={formHeader} isExpanded={isExpanded && isVendorAssessmentScreen} />
        {isVendorAssessmentScreen && (
          <Box className="pr-4 cursor-pointer">
            {isExpanded ? UpArrow("dark") : DownArrow(displayMode())}
          </Box>
        )}
      </Box>
      <form onSubmit={e => e.preventDefault()}>
        {isExpanded && (
          <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1.5rem] p-[1.5rem]">
            {/* Vendor Corporate Name */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCorporateName ? "label-error-text" : ""
                }
            `}
                text={vendorInformationLabels?.VENDOR_CORPORATE_NAME}
                htmlFor={`vendorCorporateName-${vendorKey}`}
                required={permissions.vendorCorporateName.isMandatory}
              />
              <Input
                id={`vendorCorporateName-${vendorKey}`}
                name="vendorCorporateName"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCorporateName ? "field-error-color" : ""
                } 
            `}
                value={vendorInformationFormData.vendorCorporateName ?? ""}
                disabled={isFieldDisabled("vendorCorporateName") || isExistingVendor}
                onChange={handleInputChange("vendorCorporateName")}
                onFocus={() => handleFieldFocus("vendorCorporateName")}
                onBlur={() => handleBlur("vendorCorporateName")}
                maxLength={Constants.VENDOR_CORPORATE_OR_VENDOR_NAME_MAX_LENGTH}
              />
            </Box>

            {/* Vendor First Name */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorFirstName ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_FIRST_NAME}
                htmlFor={`vendorFirstName-${vendorKey}`}
                required={permissions.vendorFirstName.isMandatory}
              />
              <Input
                id={`vendorFirstName-${vendorKey}`}
                name="vendorFirstName"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorFirstName ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorFirstName ?? ""}
                disabled={isFieldDisabled("vendorFirstName") || isExistingVendor}
                onChange={handleInputChange("vendorFirstName")}
                onFocus={() => handleFieldFocus("vendorFirstName")}
                onBlur={() => handleBlur("vendorFirstName")}
                maxLength={Constants.VENDOR_FIRST_NAME_MAX_LENGTH}
              />
            </Box>

            {/* Vendor Last Name */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorLastName ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_LAST_NAME}
                htmlFor={`vendorLastName-${vendorKey}`}
                required={permissions.vendorLastName.isMandatory}
              />
              <Input
                id={`vendorLastName-${vendorKey}`}
                name="vendorLastName"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorLastName ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorLastName ?? ""}
                disabled={isFieldDisabled("vendorLastName") || isExistingVendor}
                onChange={handleInputChange("vendorLastName")}
                onFocus={() => handleFieldFocus("vendorLastName")}
                onBlur={() => handleBlur("vendorLastName")}
                maxLength={Constants.VENDOR_LAST_NAME_MAX_LENGTH}
              />
            </Box>

            {/* Vendor Email Address */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorEmailAddress ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_EMAIL_ADDRESS}
                htmlFor={`vendorEmailAddress-${vendorKey}`}
                required={permissions.vendorEmailAddress.isMandatory}
              />
              <Input
                id={`vendorEmailAddress-${vendorKey}`}
                name="vendorEmailAddress"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorEmailAddress ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorEmailAddress ?? ""}
                disabled={isFieldDisabled("vendorEmailAddress") || isExistingVendor}
                onChange={handleInputChange("vendorEmailAddress")}
                onFocus={() => handleFieldFocus("vendorEmailAddress")}
                onBlur={() => handleBlur("vendorEmailAddress")}
                maxLength={Constants.VENDOR_EMAIL_ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Vendor Country */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCountry ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_COUNTRY}
                htmlFor={`vendorCountry-${vendorKey}`}
                required={permissions.vendorCountry.isMandatory}
              />
              {dropdownData.countries.length > 0 ? (
                <DropdownAutoSuggest
                  className={`${darkMode ? "dark" : "light"} ${
                    vendorInfoFormErrors?.vendorCountry ? "field-error-color" : ""
                  } `}
                  options={formatRawDropdownOptions(dropdownData.countries, "name", "id")}
                  value={(() => {
                    const countryId = vendorInformationFormData?.vendorCountry;
                    if (!countryId) return null;
                    const country = dropdownData.countries.find((c: any) => (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId)));
                    return country ? { label: country.name, value: (isNaN(Number(country.id)) ? country.id : Number(country.id)) } : null;
                  })()}
                  placeholder={languageText?.PLACEHOLDERS?.COMMON?.DROP_DOWN}
                  onChange={(option) => {
                    handleDropdownChange(0, "vendorCountry")(null, option);
                  }}
                  onFocus={() => handleFieldFocus("vendorCountry")}
                  onBlur={() => handleBlur("vendorCountry")}
                  isDisabled={isFieldDisabled("vendorCountry") || isExistingVendor}
                    isClearable
                  />
              ) : (
                <div className="text-gray-500 text-sm">Loading countries...</div>
              )}
            </Box>

            {/* Vendor Corporate Address */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCorporateAddress ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_CORPORATE_ADDRESS}
                htmlFor="vendor-corporate-address-input"
                required={permissions.vendorCorporateAddress.isMandatory}
              />
              <AutoComplete
                suggestions={addressSuggestions}
                inputValue={vendorInformationFormData.vendorCorporateAddress ?? ""}
                setInputValue={handleAutoCompleteInputChange("vendorCorporateAddress")}
                onSuggestionsFetchRequested={() => {
                  // Get the country name from the dropdown data instead of the ID
                  const countryId = vendorInformationFormData?.vendorCountry;
                  const countryName = dropdownData.countries.find((c: any) => 
                    (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
                  )?.name;
                  
                  // Map country name for Melissa API compatibility
                  const melissaCountryName = countryName ? getMelissaCountryName(countryName) : undefined;
                  handleSuggestionsFetchRequested(payloadRef, setAddressSuggestions, melissaCountryName);
                }}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={(suggestion: any) => suggestion?.Address?.Address1}
                renderSuggestion={(suggestion: any) => <span>{suggestion?.Address?.Address}</span>}
                onSuggestionSelected={onSuggestionSelected}
                disabled={isFieldDisabled("vendorCorporateAddress") || isExistingVendor}
                onFocus={() => handleFieldFocus("vendorCorporateAddress")}
                onBlur={() => handleBlur("vendorCorporateAddress")}
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCorporateAddress ? "field-error-color" : ""
                } `}
                inputProps={{
                  id: "vendor-corporate-address-input",
                  name: "vendorCorporateAddress",
                }}
                maxLength={Constants.ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Vendor City */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCity ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_CITY}
                htmlFor={`vendorCity-${vendorKey}`}
                required={permissions.vendorCity.isMandatory}
              />
              <Input
                id={`vendorCity-${vendorKey}`}
                name="vendorCity"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorCity ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorCity ?? ""}
                disabled={isFieldDisabled("vendorCity") || isExistingVendor}
                onChange={handleInputChange("vendorCity")}
                onFocus={() => handleFieldFocus("vendorCity")}
                onBlur={() => handleBlur("vendorCity")}
                maxLength={Constants.ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Vendor State */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorState ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_STATE}
                htmlFor={`vendorState-${vendorKey}`}
                required={permissions.vendorState.isMandatory}
              />
              <Input
                id={`vendorState-${vendorKey}`}
                name="vendorState"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorState ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorState ?? ""}
                disabled={isFieldDisabled("vendorState") || isExistingVendor}
                onChange={handleInputChange("vendorState")}
                onFocus={() => handleFieldFocus("vendorState")}
                onBlur={() => handleBlur("vendorState")}
                maxLength={Constants.ADDRESS_STATE_MAX_LENGTH}
              />
            </Box>

            {/* Vendor Zip code */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorZipCode ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.VENDOR_ZIP_CODE}
                htmlFor={`vendorZipCode-${vendorKey}`}
                required={permissions.vendorZipCode.isMandatory}
              />
              <Input
                id={`vendorZipCode-${vendorKey}`}
                name="vendorZipCode"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.vendorZipCode ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.vendorZipCode ?? ""}
                disabled={isFieldDisabled("vendorZipCode") || isExistingVendor}
                onChange={handleInputChange("vendorZipCode")}
                onFocus={() => handleFieldFocus("vendorZipCode")}
                onBlur={() => handleBlur("vendorZipCode")}
                minLength={Constants.ZIP_CODE_MIN_LENGTH}
                maxLength={Constants.ZIP_CODE_MAX_LENGTH}
              />
            </Box>

            {/* Logistics */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.logistics ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.LOGISTICS}
                htmlFor={`logistics-${vendorKey}`}
                required={permissions.logistics.isMandatory}
              />
              <Dropdown
                id={`logistics-${vendorKey}`}
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.logistics ? "field-error-color" : ""
                } `}
                options={formatRawDropdownOptions(dropdownData.logistics, "name", "id")}
                name="logistics"
                selectedValue={vendorInformationFormData?.logistics}
                onChange={handleDropdownChange(0, "logistics")}
                onFocus={() => handleFieldFocus("logistics")}
                onBlur={() => handleBlur("logistics")}
                disabled={isFieldDisabled("logistics")}
                    isClearable
                  />
            </Box>

            {/* Retail Structure */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.retailStructure ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.RETAIL_STRUCTURE}
                htmlFor={`retailStructure-${vendorKey}`}
                required={permissions.retailStructure.isMandatory}
              />
              <Dropdown
                id={`retailStructure-${vendorKey}`}
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.retailStructure ? "field-error-color" : ""
                } `}
                options={formatRawDropdownOptions(dropdownData.retails, "name", "id")}
                name="retailStructure"
                selectedValue={vendorInformationFormData?.retailStructure}
                onChange={handleDropdownChange(0, "retailStructure")}
                onFocus={() => handleFieldFocus("retailStructure")}
                onBlur={() => handleBlur("retailStructure")}
                disabled={isFieldDisabled("retailStructure")}
                    isClearable
                  />
            </Box>

            {/* Tariff */}
            <Box>
              <Label
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.tariff ? "label-error-text" : ""
                } `}
                text={vendorInformationLabels?.TARIFF}
                htmlFor={`tariff-${vendorKey}`}
                required={permissions.tariff.isMandatory}
              />
              <Input
                id={`tariff-${vendorKey}`}
                name="tariff"
                className={`${darkMode ? "dark" : "light"} ${
                  vendorInfoFormErrors?.tariff ? "field-error-color" : ""
                } `}
                value={vendorInformationFormData.tariff ?? ""}
                disabled={isFieldDisabled("tariff")}
                onChange={handleInputChange("tariff")}
                onFocus={() => handleFieldFocus("tariff")}
                onBlur={() => handleBlur("tariff")}
                maxLength={Constants.TARIFF_MAX_LENGTH}
              />
            </Box>
            <ConfirmationModal
              open={isModalOpen}
              onClose={handleCloseModal}
              onConfirm={handleConfirm}
              onDecline={handleSelectAlternative}
              confirmText={languageText?.MESSAGES?.CP_SLM_VI_ADDRESS_CONFIRM_ENTERED_BUTTON || "Confirm Entered"}
              cancelText={languageText?.MESSAGES?.CP_SLM_VI_ADDRESS_SELECT_ALTERNATIVE_BUTTON || "Select Alternative"}
              showCancelButton={true}
            />
          </Box>
        )}
      </form>
    </Box>
  );
};

export default VendorInformationForm;
